import {
	getExternalMessageDmIntegrationInfo,
	type ExternalMessageDmIntegrationInfo,
} from './thirdPartyMessaging';

export type ThirdPartyIntegrationRow = {
	externalId: string;
	integrationId: string;
	product: string;
};

/** Normalized row: profile email is applied to every entry (it is not returned per integration in the API). */
export type ThirdPartyIntegrationEntry = {
	email: string | null | undefined;
	integrationId: string;
	externalId: string;
	product: string;
};

const normalizeProductKey = (product: string) => product.trim().toLowerCase();

/**
 * Keeps the first row for each `product` (case-insensitive). Order matches the input array.
 */
function uniqueRowsByFirstOccurrence(
	integrations: ThirdPartyIntegrationRow[] = [],
): ThirdPartyIntegrationRow[] {
	const seen = new Set<string>();
	const out: ThirdPartyIntegrationRow[] = [];
	for (const row of integrations) {
		const key = normalizeProductKey(row.product);
		if (seen.has(key)) {
			continue;
		}
		seen.add(key);
		out.push(row);
	}
	return out;
}

/** Default order: Slack first, then Microsoft Teams (matches {@link getExternalMessageDmIntegrationInfo} ids). */
export const DEFAULT_MESSAGE_INTEGRATION_ID_ORDER: readonly string[] = ['slack', 'teams'];

/**
 * Tries `integrationId`s in order (Slack, then Microsoft Teams) and only returns a result when a DM
 * link can be built. If the preferred row is missing `externalId` (Slack) or `email` (Teams), that
 * candidate is skipped and the next id is tried.
 */
export function resolveExternalMessageFromIntegrations(
	integrations:
		| ThirdPartyIntegrationRow[]
		| ReadonlyArray<ThirdPartyIntegrationRow>
		| null
		| undefined,
	email: string | null | undefined,
	integrationIdOrder: readonly string[] = DEFAULT_MESSAGE_INTEGRATION_ID_ORDER,
):
	| {
			entry: ThirdPartyIntegrationEntry;
			dm: ExternalMessageDmIntegrationInfo;
	  }
	| undefined {
	const rows = uniqueRowsByFirstOccurrence(integrations ? [...integrations] : []);

	for (const id of integrationIdOrder) {
		const row = rows.find((r) => r.integrationId === id);
		if (!row) {
			continue;
		}
		const entry: ThirdPartyIntegrationEntry = {
			email,
			externalId: row.externalId,
			integrationId: row.integrationId,
			product: row.product,
		};
		const dm = getExternalMessageDmIntegrationInfo(row.integrationId, {
			email: entry.email,
			externalId: entry.externalId,
		});
		if (dm) {
			return { entry, dm };
		}
	}

	return undefined;
}
